import { useCallback, useEffect, useMemo, useState } from "react";
import { Download, PartyPopper, Search, ShieldAlert, Sparkles, X } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "./ui/dialog";
import { api, errorText } from "../lib/api";
import { fmt, fmtDate } from "../lib/constants";
import { AUDIT_GROUPS, CHECK_FIX, CHECK_SHORT, INFO_LISTS, filterItems, fixedTodayText, fmtDelta, fmtTime, slug } from "../lib/hygiene";
import { HygieneList } from "./HygieneList";
import { HygieneHealthBadge } from "./HygienePill";
import { HygieneTrend } from "./HygieneTrend";
import { initials, shortName } from "./OwnerBreakdown";

const PAGE = 200;

const CheckChip = ({ c, active, onClick }) => {
  const delta = fmtDelta(c.delta_yesterday);
  return (
    <button
      type="button"
      data-testid={`hygiene-check-${c.key}`}
      aria-pressed={active}
      disabled={!c.available}
      onClick={onClick}
      title={c.available ? `${c.label} – ${CHECK_FIX[c.key]}${delta ? ` · ${delta} since yesterday` : ""}` : `${c.label} – not available: the HubSpot property could not be found by its label`}
      className={`inline-flex shrink-0 items-center gap-2 rounded-lg border px-2.5 py-1.5 text-left text-xs transition-colors disabled:cursor-not-allowed whitespace-nowrap ${
        active ? "border-brand bg-brand/5 text-ink" : c.available ? "border-panel-border bg-panel text-slate-700 hover:border-slate-400" : "border-dashed border-panel-border text-slate-400"
      }`}
    >
      <span className="num w-4 shrink-0 text-[10px] font-bold text-slate-400">{c.group}</span>
      <span className="min-w-0 truncate font-semibold">{CHECK_SHORT[c.key] || c.label}</span>
      <span className={`num shrink-0 font-bold ${!c.available ? "text-slate-400 font-semibold" : c.count ? "text-brand" : "text-live"}`} data-testid={`hygiene-check-${c.key}-count`}>
        {c.available ? fmt(c.count) : "n/a"}
      </span>
      {delta && c.available && (
        <span className={`num shrink-0 text-[10px] font-semibold ${c.delta_yesterday > 0 ? "text-brand" : c.delta_yesterday < 0 ? "text-live" : "text-slate-400"}`} data-testid={`hygiene-check-${c.key}-delta`} title="Change since yesterday">
          {delta}
        </span>
      )}
    </button>
  );
};

// Grey, secondary chip for an informational list – never part of the total, the Δ or the pill
const InfoChip = ({ info, count, active, onClick }) => (
  <button
    type="button"
    data-testid={`hygiene-check-${info.key}`}
    aria-pressed={active}
    onClick={onClick}
    title={`${info.label} – ${info.help}`}
    className={`inline-flex shrink-0 items-center gap-2 rounded-lg border border-dashed px-2.5 py-1.5 text-left text-xs transition-colors whitespace-nowrap ${
      active ? "border-slate-500 bg-slate-100 text-ink" : "border-panel-border bg-slate-50 text-slate-500 hover:border-slate-400"
    }`}
  >
    <span className="num w-4 shrink-0 text-[10px] font-bold text-slate-400">i</span>
    <span className="min-w-0 truncate font-semibold">{info.label}</span>
    <span className="num shrink-0 font-bold text-slate-500" data-testid={`hygiene-check-${info.key}-count`}>{fmt(count || 0)}</span>
  </button>
);

// Full CRM hygiene panel (dialog): checks, owner split, searchable list with HubSpot links; staff only
export const HygienePanel = ({ open, onOpenChange, generatedAt, isAdmin = false, countFrom }) => {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [check, setCheck] = useState(null);
  const [owner, setOwner] = useState(null);
  const [query, setQuery] = useState("");
  const [limit, setLimit] = useState(PAGE);

  const load = useCallback(() => {
    let alive = true;
    api
      .get("/hygiene")
      .then((r) => alive && (setData(r.data), setError("")))
      .catch((e) => alive && setError(errorText(e)));
    return () => {
      alive = false;
    };
  }, []);
  useEffect(() => (open ? load() : undefined), [open, generatedAt, load]);
  useEffect(() => setLimit(PAGE), [check, owner, query]);
  const onIgnored = (r) => {
    toast.success(`Marked as intentional – ${fmt(r.total)} open issues now`, { duration: 4000 });
    load();
  };

  const h = data?.hygiene;
  const info = Object.values(INFO_LISTS).find((i) => i.key === check) || null;
  const gap = !!info;
  const items = useMemo(() => (info ? filterItems(h?.[info.key]?.items, { owner, query }) : filterItems(h?.items, { check, owner, query })), [h, check, owner, query, info]);
  const deltaText = fmtDelta(h?.delta_yesterday);
  const activeCheck = check && !gap ? h?.checks?.find((c) => c.key === check) : null;
  const unavailable = (h?.checks || []).filter((c) => !c.available);
  const groups = useMemo(() => {
    const out = [];
    for (const c of h?.checks || []) {
      const g = out.find((x) => x.group === c.group) || out[out.push({ group: c.group, checks: [] }) - 1];
      g.checks.push(c);
    }
    return out;
  }, [h]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl w-[calc(100vw-1rem)] sm:w-full p-0 gap-0 overflow-hidden bg-panel text-ink" data-testid="hygiene-panel">
        <div className="flex flex-col min-w-0 min-h-0 max-h-[94vh] sm:max-h-[90vh] gap-3 p-4 sm:p-6 pt-5 sm:pt-6">
          <DialogHeader className="shrink-0 min-w-0 space-y-1.5 text-left pr-7 sm:pr-8">
            <div className="flex flex-col sm:flex-row sm:items-start gap-2 sm:gap-6">
              <div className="min-w-0 flex-1 space-y-1.5">
                <DialogTitle className="flex items-center gap-2 flex-wrap text-base sm:text-xl font-bold" data-testid="hygiene-panel-title">
                  <ShieldAlert className="w-5 h-5 text-brand shrink-0" /> CRM hygiene
                  <span className="text-slate-400 font-medium text-sm">· since {fmtDate(h?.since || countFrom)}</span>
                  {h && <HygieneHealthBadge total={h.total} broken={!!h.error} />}
                </DialogTitle>
                <DialogDescription className="text-slate-600 text-sm" data-testid="hygiene-panel-summary">
                  {h ? (
                    <>
                      <b className="num text-ink" data-testid="hygiene-panel-total">{fmt(h.total)}</b> open {h.total === 1 ? "issue" : "issues"} on <b className="num text-ink">{fmt(h.records)}</b> records
                      {deltaText && <span className={h.delta_yesterday > 0 ? "text-brand" : h.delta_yesterday < 0 ? "text-live" : "text-slate-400"} data-testid="hygiene-panel-delta"> · {deltaText} since yesterday</span>}
                      <span className="text-live" data-testid="hygiene-panel-fixed"> · {fmt(h.fixed_7d || 0)} fixed in 7 days</span>
                      {h.fixed_today > 0 && (
                        <span data-testid="hygiene-panel-fixed-today" key={h.fixed_today} title={fixedTodayText(h.fixed_today_by_check)} className="hygiene-celebrate ml-1.5 inline-flex items-center gap-1 rounded-full bg-live/10 text-live px-2 py-0.5 text-xs font-bold align-middle">
                          <PartyPopper className="w-3.5 h-3.5" /> {fmt(h.fixed_today)} fixed today
                        </span>
                      )}
                      {h.ignored?.count > 0 && <span className="text-slate-500" data-testid="hygiene-panel-ignored" title="Marked as intentional by the admin – hidden until they change"> · {fmt(h.ignored.count)} ignored</span>}
                      <span className="text-slate-400"> · scanned {fmt(h.scanned?.deals)} deals, {fmt(h.scanned?.companies)} companies · {fmtTime(h.generated_at)}</span>
                    </>
                  ) : error ? (
                    <span className="text-brand" data-testid="hygiene-panel-error">{error}</span>
                  ) : (
                    "Loading the latest audit…"
                  )}
                </DialogDescription>
              </div>
              {h && <HygieneTrend trend={h.trend} className="shrink-0 sm:pt-1" />}
            </div>
          </DialogHeader>

          {data?.note && (
            <div className="shrink-0 rounded-xl border border-amber-300/70 bg-amber-50 px-3 py-2 text-sm text-amber-900 whitespace-pre-wrap" data-testid="hygiene-note">
              <span className="eyebrow block text-amber-800/80 mb-0.5">Team rules</span>
              {data.note}
            </div>
          )}

          {h && (
            <div className="shrink-0 flex sm:flex-wrap gap-x-4 gap-y-2 overflow-x-auto sm:overflow-visible thin-scroll -mx-4 px-4 sm:mx-0 sm:px-0 pb-1 sm:pb-0" data-testid="hygiene-checks">
              {groups.map((g) => (
                <div key={g.group} className="min-w-0 shrink-0 sm:shrink" data-testid={`hygiene-group-${g.group}`}>
                  <div className="eyebrow !text-[9px] mb-1 truncate hidden sm:block">{g.group} · {AUDIT_GROUPS[g.group]}</div>
                  <div className="flex sm:flex-wrap gap-1.5">
                    {g.checks.map((c) => <CheckChip key={c.key} c={c} active={check === c.key} onClick={() => setCheck(check === c.key ? null : c.key)} />)}
                    {INFO_LISTS[g.group] && h[INFO_LISTS[g.group].key] && (
                      <InfoChip info={INFO_LISTS[g.group]} count={h[INFO_LISTS[g.group].key].count} active={check === INFO_LISTS[g.group].key} onClick={() => setCheck(check === INFO_LISTS[g.group].key ? null : INFO_LISTS[g.group].key)} />
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
          {unavailable.length > 0 && (
            <div className="shrink-0 text-xs text-slate-500" data-testid="hygiene-unavailable">
              Not available (HubSpot property not found by label): {unavailable.map((c) => c.label).join(", ")}.
            </div>
          )}

          {h?.owners?.length > 0 && (
            <div className="shrink-0 flex items-center gap-1.5 overflow-x-auto thin-scroll -mx-4 px-4 sm:mx-0 sm:px-0 pb-0.5" data-testid="hygiene-owners">
              <span className="text-[10px] uppercase tracking-wider font-bold text-slate-500 shrink-0 mr-1">By owner</span>
              {h.owners.map((o) => {
                const on = owner === o.name;
                const ns = h.owner_name_scores?.[o.name];
                return (
                  <button
                    key={o.name}
                    type="button"
                    data-testid={`hygiene-owner-${slug(o.name)}`}
                    aria-pressed={on}
                    onClick={() => setOwner(on ? null : o.name)}
                    title={`${on ? "Show all owners" : `Only records owned by ${o.name} (${o.count} issues)`}${ns ? ` · AI deal-name quality ${ns.avg}/4 over ${ns.n} deals` : ""}`}
                    className={`shrink-0 inline-flex items-center gap-1.5 rounded-full border pl-1 pr-2.5 py-0.5 text-xs font-semibold transition-colors ${on ? "border-ink bg-ink text-page" : "border-panel-border bg-panel text-slate-700 hover:border-slate-400"}`}
                  >
                    <span className={`w-5 h-5 rounded-full text-[9px] font-bold flex items-center justify-center ${on ? "bg-page/20 text-page" : "bg-ink/10 text-ink"}`}>{initials(o.name)}</span>
                    {shortName(o.name)} <b className="num">{fmt(o.count)}</b>
                    {ns && <span className={`num text-[10px] ${on ? "text-page/70" : ns.avg < 2 ? "text-brand" : ns.avg < 3 ? "text-amber-700" : "text-live"}`} data-testid={`hygiene-owner-score-${slug(o.name)}`}>★{ns.avg}</span>}
                    {on && <X className="w-3 h-3" />}
                  </button>
                );
              })}
            </div>
          )}

          <div className="shrink-0 flex flex-col sm:flex-row sm:items-center gap-2">
            <label className="flex-1 flex items-center gap-2 rounded-lg border border-panel-border bg-slate-50 px-3 py-1.5 text-sm focus-within:border-brand">
              <Search className="w-4 h-4 text-slate-400 shrink-0" />
              <input data-testid="hygiene-search" value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search deal, venue, company, owner or ID…" className="flex-1 min-w-0 bg-transparent outline-none text-ink placeholder:text-slate-400" />
              {query && <button type="button" data-testid="hygiene-search-clear" onClick={() => setQuery("")} className="text-slate-400 hover:text-ink"><X className="w-4 h-4" /></button>}
            </label>
            <span className="num text-xs text-slate-500 shrink-0" data-testid="hygiene-shown">
              {fmt(items.length)} {info ? info.unit : check ? "findings" : "records"}{owner || check || query ? " match" : ""}
            </span>
            {isAdmin && (
              <a
                href={`${api.defaults.baseURL}/admin/hygiene/export/${check || "all"}.csv`}
                data-testid="hygiene-csv-link"
                className="inline-flex items-center gap-1.5 rounded-full border border-panel-border px-3 py-1.5 text-xs font-semibold text-slate-700 hover:border-slate-400 shrink-0"
                title="Download this check as CSV (admin)"
              >
                <Download className="w-3.5 h-3.5" /> CSV
              </a>
            )}
          </div>
          {info && (
            <div className="shrink-0 rounded-lg bg-slate-50 border border-dashed border-panel-border px-3 py-2 text-xs text-slate-600" data-testid="hygiene-check-fix">
              <b>{info.label}.</b> {info.help}
            </div>
          )}
          {activeCheck && (
            <div className="shrink-0 rounded-lg bg-brand/5 border border-brand/20 px-3 py-2 text-xs text-ink" data-testid="hygiene-check-fix">
              <b>{activeCheck.label}.</b> {CHECK_FIX[activeCheck.key]}
              {isAdmin && <span className="text-slate-500"> Intentional? Use <i>Ignore</i> on a row{activeCheck.key === "duplicate" ? " or on a whole group" : ""} – it stays hidden until the finding changes.</span>}
            </div>
          )}

          <div className="flex-1 min-h-[30vh] sm:min-h-0 overflow-y-auto thin-scroll panel" data-testid="hygiene-list">
            {h && <HygieneList items={items} check={gap ? null : check} limit={limit} onMore={() => setLimit((l) => l + PAGE)} canIgnore={isAdmin && !gap} onIgnored={onIgnored} />}
          </div>
          <div className="shrink-0 text-[11px] text-slate-400 hidden sm:block">
            Counted per deal / per company for records created since {fmtDate(h?.since || countFrom)}; test records excluded. Click a name to open it in HubSpot. “Fixed today” = open at the first check of today, gone now; “Fixed” (7 days) = flagged on an earlier day, clean now. “Ignored” = marked as intentional by the admin (comes back if it changes). <Sparkles className="inline w-3 h-3 text-brand -mt-0.5" /> = AI (Jev) suggestion with its confidence; ★ = average AI deal-name quality 1–4 per owner.
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
